import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import MetaData from '../../layouts/MetaData';
import RoleShell from '../../components/Enrollment/RoleShell';
import { DR_API, DEVICE_TYPES, EC_PENDING, STATUS, errMsg, fmtDateTime, patientLabel, typeLabel } from '../../components/DeviceRequests/deviceRequestApi';
import { useSortableTable, SortableTh, personName } from '../../components/SortableTable';

const COLS = [
    ['patient', 'Patient', { type: 'name', value: (r) => personName(r.patient) }],
    ['device', 'Device type', { type: 'string', value: (r) => (r.deviceTypes || []).join(',') }],
    ['provider', 'Provider', { type: 'name', value: (r) => r.providerName }],
    ['sentAt', 'Date sent', { type: 'date', value: (r) => r.sentAt }],
    ['batch', 'Batch', { type: 'string', value: (r) => r.batch?.batchNo }],
    ['age', 'Age (days)', { type: 'number', value: (r) => r.ageDays || 0, defaultDir: 'desc' }],
    ['status', 'Status', { type: 'string', value: (r) => r.status }],
];
const EC_SORT_COLUMNS = Object.fromEntries(COLS.map(([k, , c]) => [k, c]));

/** Enrollment Coordinator: pending device requests (SENT_TO_EC + IN_PROGRESS + PARTIAL). */
const EcPendingRequests = () => {
    const [rows, setRows] = useState([]);
    const [orgs, setOrgs] = useState([]);
    const [orgId, setOrgId] = useState('');
    const [deviceType, setDeviceType] = useState('');
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(DR_API, { params: { status: EC_PENDING, limit: 200, ...(orgId ? { organizationId: orgId } : {}), ...(deviceType ? { deviceType } : {}) } });
            setRows(data.requests || []);
        } catch (err) { setError(errMsg(err)); }
    }, [orgId, deviceType]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        axios.get(`${DR_API.replace('/device-requests', '')}/organizations/mine`)
            .then((r) => setOrgs(Array.isArray(r.data) ? r.data : r.data.organizations || r.data.results || []))
            .catch(() => {});
    }, []);

    const tableSort = useSortableTable(rows, { id: 'deviceRequests.ecPending', columns: EC_SORT_COLUMNS, defaultSort: { key: 'age', dir: 'desc' } });

    return (
        <RoleShell title="Device Requests — Pending">
            <MetaData title="Device Requests — Pending" />
            <h5>Device Requests — Pending <span className="badge badge-danger">{rows.length}</span></h5>
            <div className="form-row mb-2">
                <div className="col-md-3">
                    <select className="form-control form-control-sm" aria-label="Organization" value={orgId} onChange={(e) => setOrgId(e.target.value)}>
                        <option value="">All my organizations</option>
                        {orgs.map((o) => <option key={o._id || o.id} value={o._id || o.id}>{o.name}</option>)}
                    </select>
                </div>
                <div className="col-md-3">
                    <select className="form-control form-control-sm" aria-label="Device type" value={deviceType} onChange={(e) => setDeviceType(e.target.value)}>
                        <option value="">All device types</option>
                        {DEVICE_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                </div>
            </div>
            {error && <div className="alert alert-danger py-2">{error}</div>}
            <table className="table table-sm table-striped table-hover" data-testid="dr-ec-list">
                <thead>
                    <tr>{COLS.map(([k, l]) => (
                        <SortableTh key={k} sortKey={k} sort={tableSort}>{l}</SortableTh>
                    ))}<th>Organization</th></tr>
                </thead>
                <tbody>
                    {tableSort.rows.map((r) => (
                        <tr key={r._id}>
                            <td><Link to={`/enrollment/device-requests/${r._id}`}>{patientLabel(r.patient)}</Link></td>
                            <td>{(r.items || []).map((i) => <div key={i._id}>{typeLabel(i.deviceType)}{i.status === 'FULFILLED' ? ' ✓' : ''}</div>)}</td>
                            <td>{r.providerName}</td><td>{fmtDateTime(r.sentAt)}</td><td>{r.batch?.batchNo}</td><td>{r.ageDays}</td>
                            <td><span className={`badge badge-${(STATUS[r.status] || {}).bg}`}>{(STATUS[r.status] || {}).label}</span></td>
                            <td>{r.organization?.name}</td>
                        </tr>
                    ))}
                    {!tableSort.rows.length && <tr><td colSpan={8} className="text-muted">No pending device requests.</td></tr>}
                </tbody>
            </table>
        </RoleShell>
    );
};

export default EcPendingRequests;
